import React, { useMemo, useState } from 'react';
import {
  Box,
  HStack,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Button,
  ButtonGroup,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tfoot,
  Tr,
  Th,
  Td,
  Text,
  Badge,
  Input,
} from '@chakra-ui/react';
import { PlaidTransaction, TransactionSort, TransactionSortColumn } from '../../types/transactions';
import { isCheck, isZelle, isTransfer, getFlowDirection, getCheckNumber } from '../../utils/payment-type';
import TransactionTable from '../transactions/TransactionTable';

type PaymentTab = 'checks' | 'zelle' | 'transfer';
type ViewMode = 'list' | 'pivot';

interface CategoryOption { primary: string; detailed: string; }

interface PaymentsReportProps {
  transactions: PlaidTransaction[];
  initialTab?: PaymentTab;
  hideTabs?: boolean;
  // Edit capabilities — same as TransactionTable
  isEditable?: boolean;
  onEdit?: (transaction: PlaidTransaction) => void;
  onInlineSave?: (id: string, updates: { merchant?: string; pfc_primary?: string; pfc_detailed?: string }) => Promise<void>;
  onSaveVendor?: (id: string, vendor: string) => Promise<void>;
  onRevert?: (transaction: PlaidTransaction) => void;
  availableCategories?: CategoryOption[];
  recentlyEditedId?: string | null;
}

const formatCurrency = (val: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(Math.abs(val));

export const PaymentsReport: React.FC<PaymentsReportProps> = ({
  transactions,
  initialTab = 'checks',
  hideTabs = false,
  isEditable = false,
  onEdit,
  onInlineSave,
  onSaveVendor,
  onRevert,
  availableCategories,
  recentlyEditedId,
}) => {
  const [activeTab, setActiveTab] = useState<PaymentTab>(initialTab);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<TransactionSort>({ column: 'date', direction: 'desc' });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllMode, setSelectAllMode] = useState<'none' | 'displayed' | 'all'>('none');

  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const theadBg = useColorModeValue('gray.50', 'gray.800');
  const tfootBg = useColorModeValue('gray.100', 'gray.900');

  const sortByIncomeAlpha = (a: PlaidTransaction, b: PlaidTransaction) => {
    const aIsIncome = a.amount < 0;
    const bIsIncome = b.amount < 0;
    if (aIsIncome !== bIsIncome) return aIsIncome ? -1 : 1;
    const aName = (a.merchantName || a.name || '').toLowerCase();
    const bName = (b.merchantName || b.name || '').toLowerCase();
    return aName.localeCompare(bName);
  };

  const checks = useMemo(
    () => transactions.filter(isCheck).sort(sortByIncomeAlpha),
    [transactions],
  );

  const zelles = useMemo(
    () => transactions.filter(isZelle).sort(sortByIncomeAlpha),
    [transactions],
  );

  const transfers = useMemo(
    () => transactions.filter(isTransfer).sort(sortByIncomeAlpha),
    [transactions],
  );

  const activeData = activeTab === 'checks' ? checks : activeTab === 'zelle' ? zelles : transfers;

  const filtered = useMemo(() => {
    if (!search.trim()) return activeData;
    const q = search.toLowerCase();
    return activeData.filter(
      t =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.merchantName || '').toLowerCase().includes(q) ||
        (getCheckNumber(t) || '').includes(q),
    );
  }, [activeData, search]);

  const sortedFiltered = useMemo(() => {
    const copy = [...filtered];
    const { column, direction } = sort;
    const mult = direction === 'asc' ? 1 : -1;
    copy.sort((a, b) => {
      let aVal: any, bVal: any;
      switch (column) {
        case 'date':         aVal = a.date;       bVal = b.date;       break;
        case 'amount':       aVal = a.amount;     bVal = b.amount;     break;
        // Missing numbers sort as -1: last in desc, first in asc.
        case 'checkNumber':  aVal = Number(getCheckNumber(a) ?? -1); bVal = Number(getCheckNumber(b) ?? -1); break;
        case 'merchantName': aVal = (a.merchantName || a.name || '').toLowerCase(); bVal = (b.merchantName || b.name || '').toLowerCase(); break;
        case 'personalFinanceCategoryPrimary': aVal = (a.personalFinanceCategoryPrimary || '').toLowerCase(); bVal = (b.personalFinanceCategoryPrimary || '').toLowerCase(); break;
        case 'paymentChannel': aVal = (a.paymentChannel || '').toLowerCase(); bVal = (b.paymentChannel || '').toLowerCase(); break;
        default: return 0;
      }
      if (aVal < bVal) return -mult;
      if (aVal > bVal) return mult;
      return 0;
    });
    return copy;
  }, [filtered, sort]);

  const pivotRows = useMemo(() => {
    const byMonth: Record<string, { in: number; out: number }> = {};
    for (const t of activeData) {
      const month = t.date.substring(0, 7);
      if (!byMonth[month]) byMonth[month] = { in: 0, out: 0 };
      if (t.amount < 0) byMonth[month].in += Math.abs(t.amount);
      else if (t.amount > 0) byMonth[month].out += t.amount;
    }
    return Object.entries(byMonth)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, vals]) => ({ month, ...vals }));
  }, [activeData]);

  const stats = useMemo(() => {
    const income = activeData.filter(t => getFlowDirection(t) === 'INCOME');
    const expense = activeData.filter(t => getFlowDirection(t) === 'EXPENSE');
    const totalIn = income.reduce((s, t) => s + Math.abs(t.amount), 0);
    const totalOut = expense.reduce((s, t) => s + Math.abs(t.amount), 0);
    return { count: activeData.length, incomeCount: income.length, expenseCount: expense.length, totalIn, totalOut };
  }, [activeData]);

  return (
    <VStack align="stretch" spacing={4}>
      {/* Tabs */}
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          {!hideTabs && (
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              colorScheme={activeTab === 'checks' ? 'blue' : 'gray'}
              variant={activeTab === 'checks' ? 'solid' : 'outline'}
              onClick={() => { setActiveTab('checks'); setSearch(''); }}
            >
              Checks {checks.length > 0 && `(${checks.length})`}
            </Button>
            <Button
              colorScheme={activeTab === 'zelle' ? 'blue' : 'gray'}
              variant={activeTab === 'zelle' ? 'solid' : 'outline'}
              onClick={() => { setActiveTab('zelle'); setSearch(''); }}
            >
              Zelle {zelles.length > 0 && `(${zelles.length})`}
            </Button>
            <Button
              colorScheme={activeTab === 'transfer' ? 'blue' : 'gray'}
              variant={activeTab === 'transfer' ? 'solid' : 'outline'}
              onClick={() => { setActiveTab('transfer'); setSearch(''); }}
            >
              Transfers {transfers.length > 0 && `(${transfers.length})`}
            </Button>
          </ButtonGroup>
          )}

          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              colorScheme={viewMode === 'list' ? 'gray' : 'gray'}
              variant={viewMode === 'list' ? 'solid' : 'outline'}
              onClick={() => setViewMode('list')}
            >
              List
            </Button>
            <Button
              colorScheme={viewMode === 'pivot' ? 'gray' : 'gray'}
              variant={viewMode === 'pivot' ? 'solid' : 'outline'}
              onClick={() => setViewMode('pivot')}
            >
              Pivot
            </Button>
          </ButtonGroup>
        </HStack>

        {viewMode === 'list' && (
          <Input
            size="sm"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            maxW="240px"
            borderRadius="md"
          />
        )}
      </HStack>

      {/* Stats */}
      {stats.count > 0 ? (
        <SimpleGrid columns={4} spacing={3}>
          <Stat size="sm" px={3} py={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel>Total</StatLabel>
            <StatNumber fontSize="lg">{stats.count}</StatNumber>
            <StatHelpText mb={0}>transactions</StatHelpText>
          </Stat>
          <Stat size="sm" px={3} py={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel color="green.600">Income</StatLabel>
            <StatNumber fontSize="lg" color="green.600">{formatCurrency(stats.totalIn)}</StatNumber>
            <StatHelpText mb={0}>{stats.incomeCount} transactions</StatHelpText>
          </Stat>
          <Stat size="sm" px={3} py={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel color="red.500">Expense</StatLabel>
            <StatNumber fontSize="lg" color="red.500">{formatCurrency(stats.totalOut)}</StatNumber>
            <StatHelpText mb={0}>{stats.expenseCount} transactions</StatHelpText>
          </Stat>
          <Stat size="sm" px={3} py={2} borderWidth="1px" borderRadius="md" borderColor={borderColor}>
            <StatLabel>Net</StatLabel>
            <StatNumber
              fontSize="lg"
              color={stats.totalIn - stats.totalOut >= 0 ? 'green.600' : 'red.500'}
            >
              {formatCurrency(stats.totalIn - stats.totalOut)}
            </StatNumber>
            <StatHelpText mb={0}>income − expense</StatHelpText>
          </Stat>
        </SimpleGrid>
      ) : (
        <Box py={10} textAlign="center" color="gray.400">
          No {activeTab === 'checks' ? 'check' : activeTab === 'zelle' ? 'Zelle' : 'transfer'} transactions found in the selected date range.
        </Box>
      )}

      {/* Pivot view */}
      {viewMode === 'pivot' && (
        pivotRows.length > 0 ? (
          <Box overflowX="auto" borderWidth="1px" borderRadius="lg" borderColor={borderColor}>
            <Table variant="simple" size="sm">
              <Thead position="sticky" top={0} bg={theadBg} zIndex={1}>
                <Tr bg={theadBg}>
                  <Th minW="100px">Month</Th>
                  <Th isNumeric minW="130px" color="green.600">Income</Th>
                  <Th isNumeric minW="130px" color="red.500">Expense</Th>
                  <Th isNumeric minW="130px" fontWeight="bold">Net</Th>
                </Tr>
              </Thead>
              <Tbody>
                {pivotRows.map(row => {
                  const net = row.in - row.out;
                  return (
                    <Tr key={row.month} _hover={{ bg: hoverBg }}>
                      <Td fontWeight="medium">{row.month}</Td>
                      <Td isNumeric color={row.in > 0 ? 'green.600' : 'gray.300'}>
                        {row.in > 0 ? `+${formatCurrency(row.in)}` : '-'}
                      </Td>
                      <Td isNumeric color={row.out > 0 ? 'red.500' : 'gray.300'}>
                        {row.out > 0 ? `-${formatCurrency(row.out)}` : '-'}
                      </Td>
                      <Td isNumeric fontWeight="bold" color={net > 0 ? 'green.600' : net < 0 ? 'red.500' : 'inherit'}>
                        {net > 0 ? `+${formatCurrency(net)}` : net < 0 ? `-${formatCurrency(Math.abs(net))}` : formatCurrency(0)}
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
              <Tfoot>
                <Tr bg={tfootBg}>
                  <Th fontWeight="black">Total</Th>
                  <Th isNumeric fontWeight="black" color="green.600">
                    +{formatCurrency(stats.totalIn)}
                  </Th>
                  <Th isNumeric fontWeight="black" color="red.500">
                    -{formatCurrency(stats.totalOut)}
                  </Th>
                  <Th
                    isNumeric
                    fontWeight="black"
                    fontSize="md"
                    color={stats.totalIn - stats.totalOut > 0 ? 'green.600' : stats.totalIn - stats.totalOut < 0 ? 'red.500' : 'inherit'}
                  >
                    {stats.totalIn - stats.totalOut >= 0
                      ? `+${formatCurrency(stats.totalIn - stats.totalOut)}`
                      : `-${formatCurrency(Math.abs(stats.totalIn - stats.totalOut))}`}
                  </Th>
                </Tr>
              </Tfoot>
            </Table>
          </Box>
        ) : (
          <Box py={10} textAlign="center" color="gray.400">
            No {activeTab === 'checks' ? 'check' : activeTab === 'zelle' ? 'Zelle' : 'transfer'} transactions found in the selected date range.
          </Box>
        )
      )}

      {/* List view — uses TransactionTable for full edit support */}
      {viewMode === 'list' && (
        <TransactionTable
          transactions={sortedFiltered}
          selectedIds={selectedIds}
          selectAllMode={selectAllMode}
          totalCount={sortedFiltered.length}
          sort={sort}
          onToggleSort={(col) =>
            setSort(prev => ({
              column: col,
              direction: prev.column === col && prev.direction === 'desc' ? 'asc' : 'desc',
            }))
          }
          onSelectTransaction={(id) =>
            setSelectedIds(prev => {
              const next = new Set(prev);
              if (next.has(id)) next.delete(id); else next.add(id);
              return next;
            })
          }
          onSelectAllDisplayed={() => {
            if (selectedIds.size === sortedFiltered.length && sortedFiltered.length > 0) {
              setSelectedIds(new Set()); setSelectAllMode('none');
            } else {
              setSelectedIds(new Set(sortedFiltered.map(t => t.id))); setSelectAllMode('displayed');
            }
          }}
          onSelectAll={() => {
            setSelectedIds(new Set(sortedFiltered.map(t => t.id))); setSelectAllMode('all');
          }}
          onEdit={onEdit}
          onInlineSave={onInlineSave}
          onSaveVendor={onSaveVendor}
          onRevert={onRevert}
          isEditable={isEditable}
          availableCategories={availableCategories}
          recentlyEditedId={recentlyEditedId}
          showCheckNumber={activeTab === 'checks'}
        />
      )}
    </VStack>
  );
};
